// src/components/screener/ProvenScreensDropdown.jsx
// New file — Proven Screening Methods dropdown. Placed next to the existing preset scan
// buttons (same conceptual category: "load a strategy lens"), not mixed into the Signal/Sector
// attribute-filter row. See screenerConfig.js's PROVEN_SCREENS for the methodology sources.
//
// Honesty discipline, same as the field registry: selecting a 'pending' screen does NOT
// silently apply nothing — it shows exactly which criteria are missing and why, so a member
// never wonders whether the empty/unfiltered result means "no matches" or "this doesn't work yet."

import React, { useState } from 'react';
import { S, PROVEN_SCREENS } from '../../constants/screenerConfig';

export default function ProvenScreensDropdown({ onSelectScreen, activeScreenKey }) {
  const [open, setOpen] = useState(false);
  const active = PROVEN_SCREENS.find(p => p.key === activeScreenKey) || null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
          background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
          color: active ? S.purple : S.muted,
          border: `1px solid ${active ? S.purple : S.border2}`,
          cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        📚 {active ? active.label : 'Proven Screens'} <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', left: 0, top: '110%', zIndex: 50,
          background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 10,
          padding: '10px 12px', minWidth: 340, maxHeight: 420, overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}>
          <div style={{ fontSize: 11, color: S.muted, fontWeight: 700, marginBottom: 8, letterSpacing: '0.06em' }}>
            PROVEN SCREENING METHODS
          </div>
          {activeScreenKey && (
            <button
              onClick={() => { onSelectScreen(null); setOpen(false); }}
              style={{
                width: '100%', textAlign: 'left', padding: '6px 8px', marginBottom: 4,
                background: 'transparent', border: 'none', color: S.muted, fontSize: 12,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              ✕ Clear (back to raw signals)
            </button>
          )}
          {PROVEN_SCREENS.map(p => (
            <div
              key={p.key}
              onClick={() => { onSelectScreen(p.key); setOpen(false); }}
              style={{
                padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                background: activeScreenKey === p.key ? 'rgba(99,102,241,0.1)' : 'transparent',
                marginBottom: 2,
              }}
              onMouseEnter={e => { if (activeScreenKey !== p.key) e.currentTarget.style.background = S.bg; }}
              onMouseLeave={e => { if (activeScreenKey !== p.key) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: S.text }}>{p.label}</span>
                <span style={{
                  fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                  background: p.status === 'partial' ? 'rgba(245,158,11,0.15)' : 'rgba(139,139,167,0.15)',
                  color: p.status === 'partial' ? S.amber : S.subtle,
                }}>
                  {p.status === 'partial' ? 'PARTIAL — SOME CRITERIA LIVE' : 'CRITERIA PENDING'}
                </span>
              </div>
              <div style={{ fontSize: 11, color: S.subtle, marginTop: 2 }}>{p.source}</div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div style={{
          marginTop: 8, padding: '10px 14px', borderRadius: 8,
          background: active.status === 'partial' ? 'rgba(245,158,11,0.06)' : 'rgba(139,139,167,0.06)',
          border: `1px solid ${active.status === 'partial' ? 'rgba(245,158,11,0.2)' : 'rgba(139,139,167,0.15)'}`,
          fontSize: 12, color: S.muted, maxWidth: 460,
        }}>
          <strong style={{ color: active.status === 'partial' ? S.amber : S.subtle }}>
            {active.label} — {active.source}
          </strong>
          <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
            {active.criteria.map((c, i) => <li key={i} style={{ marginBottom: 2 }}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
